import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ProvidersService } from '../services/ProvidersService';
import { SupportService } from '../services/SupportService';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CODE_LENGTH = 5;

function ClaimBusinessDialog({ open, onClose, provider, initialBusinessName, initialEmail, initialPhone }) {
  const navigate = useNavigate();
  const [businessName, setBusinessName] = useState(initialBusinessName || provider?.name || '');
  const [email, setEmail] = useState(initialEmail || '');
  const [phone, setPhone] = useState(initialPhone || '');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState('form'); // 'form', 'success', 'contact-us', 'phone-offer', 'phone-code', 'phone-throttled', 'already-claimed', 'throttled', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [userConsented, setUserConsented] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [phoneVerificationAvailable, setPhoneVerificationAvailable] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!phone.trim()) {
      newErrors.phone = 'Phone is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const result = await ProvidersService.requestProviderClaim(provider.id, {
        businessName,
        email,
        phone,
      });

      if (result?.matched === true) {
        setState('success');
      } else if (result?.matched === false) {
        if (result?.phoneVerificationAvailable) {
          setPhoneVerificationAvailable(true);
          setState('phone-offer');
        } else {
          setState('contact-us');
        }
      } else if (result?.status === 409 && result?.data?.alreadyClaimed) {
        setState('already-claimed');
      } else if (result?.status === 429) {
        setState('throttled');
      } else {
        setState('error');
        setErrorMessage(
          result?.data?.message || 'An unexpected error occurred. Please try again.'
        );
      }
    } catch (err) {
      setState('error');
      setErrorMessage('An unexpected error occurred. Please try again.');
      console.error('Error requesting provider claim:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleContactUsYes = async () => {
    setLoading(true);
    try {
      const ticketData = {
        role: 'Providers',
        issue: 'General',
        email,
        title: `Business claim request: ${businessName}`,
        additionalInfo: `Provider ID: ${provider.id}\nFriendly URL: ${provider.friendlyUrl || 'N/A'}\nBusiness Name: ${businessName}\nEmail: ${email}\nPhone: ${phone}`,
        priority: 'Normal',
      };

      const response = await SupportService.submitSupportTicket(ticketData);

      if (response?.status === 201) {
        setUserConsented(true);
      } else {
        setState('error');
        setErrorMessage('Failed to submit support request. Please try again.');
      }
    } catch (err) {
      setState('error');
      setErrorMessage('An error occurred while submitting your request. Please try again.');
      console.error('Error submitting support ticket:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneOfferYes = async () => {
    setLoading(true);
    try {
      const result = await ProvidersService.requestClaimCall(provider.id, phone);

      if (result?.status === 202) {
        setVerificationCode('');
        setCodeError('');
        setAttemptsLeft(null);
        setState('phone-code');
      } else if (result?.status === 429) {
        setState('phone-throttled');
      } else if (result?.status === 503) {
        setState('error');
        setErrorMessage('Phone verification is currently unavailable. Please try again later.');
      } else if (result?.status === 409 && result?.data?.alreadyClaimed) {
        setState('already-claimed');
      } else if (result?.status === 400) {
        setState('error');
        setErrorMessage('The phone number does not match our records.');
      } else {
        setState('error');
        setErrorMessage(result?.data?.message || 'An error occurred. Please try again.');
      }
    } catch (err) {
      setState('error');
      setErrorMessage('An error occurred while requesting the call. Please try again.');
      console.error('Error requesting claim call:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneOfferNo = () => {
    setState('contact-us');
  };

  const handleRequestCode = async () => {
    setLoading(true);
    try {
      const result = await ProvidersService.requestClaimCall(provider.id, phone);

      if (result?.status === 202) {
        setCodeError('');
      } else if (result?.status === 429) {
        setState('error');
        setErrorMessage("We've already called this number several times today. Please try again tomorrow or contact us.");
      } else if (result?.status === 503) {
        setState('error');
        setErrorMessage('Phone verification is currently unavailable. Please try again later.');
      } else if (result?.status === 409 && result?.data?.alreadyClaimed) {
        setState('already-claimed');
      } else if (result?.status === 400) {
        setState('error');
        setErrorMessage('The phone number does not match our records.');
      } else {
        setState('error');
        setErrorMessage(result?.data?.message || 'An error occurred. Please try again.');
      }
    } catch (err) {
      setState('error');
      setErrorMessage('An error occurred while requesting the call. Please try again.');
      console.error('Error requesting claim call:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== CODE_LENGTH) {
      setCodeError('Please enter the 5-digit code.');
      return;
    }

    setLoading(true);
    try {
      const result = await ProvidersService.verifyClaimCall(provider.id, verificationCode);

      if (result?.status === 200 && result?.data?.invitation) {
        navigate(`/register?invitation=${encodeURIComponent(result.data.invitation)}`);
        handleClose();
      } else if (result?.status === 400) {
        setAttemptsLeft(result?.data?.attemptsLeft || null);
        setCodeError(`That code didn't match. ${result?.data?.attemptsLeft ? result.data.attemptsLeft + ' attempts left.' : ''}`);
      } else if (result?.status === 410) {
        setCodeError('That code has expired. Request a new call.');
      } else if (result?.status === 409 && result?.data?.alreadyClaimed) {
        setState('already-claimed');
      } else {
        setState('error');
        setErrorMessage(result?.data?.message || 'An error occurred. Please try again.');
      }
    } catch (err) {
      setState('error');
      setErrorMessage('An error occurred while verifying the code. Please try again.');
      console.error('Error verifying claim call:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setBusinessName(initialBusinessName || provider?.name || '');
    setEmail(initialEmail || '');
    setPhone(initialPhone || '');
    setErrors({});
    setState('form');
    setErrorMessage('');
    setUserConsented(false);
    setVerificationCode('');
    setCodeError('');
    setAttemptsLeft(null);
    setPhoneVerificationAvailable(false);
    onClose();
  };

  const isFormValid = businessName.trim() && email.trim() && emailRegex.test(email) && phone.trim();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Claim This Business</DialogTitle>
      <DialogContent>
        {state === 'form' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Business Name"
              value={businessName}
              onChange={(e) => {
                setBusinessName(e.target.value);
                if (errors.businessName) {
                  setErrors({ ...errors, businessName: '' });
                }
              }}
              error={!!errors.businessName}
              helperText={errors.businessName}
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) {
                  setErrors({ ...errors, email: '' });
                }
              }}
              error={!!errors.email}
              helperText={errors.email}
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Phone"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                if (errors.phone) {
                  setErrors({ ...errors, phone: '' });
                }
              }}
              error={!!errors.phone}
              helperText={errors.phone}
              disabled={loading}
            />
          </Box>
        )}

        {state === 'success' && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Success!
            </Alert>
            <Typography>
              A verification email has been sent to the address on file for this business. Please click the link in that email to finish claiming the business.
            </Typography>
          </Box>
        )}

        {state === 'contact-us' && !userConsented && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ mb: 2 }}>
              Looks like we need some additional info here. Is it okay for our team to contact you?
            </Typography>
          </Box>
        )}

        {state === 'contact-us' && userConsented && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success">
              Thank you! Our support team will contact you shortly at {email}.
            </Alert>
          </Box>
        )}

        {state === 'phone-offer' && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ mb: 2 }}>
              We couldn't match the email, but the phone number matches our records. We'll call {phone} with a five-digit code. Is that okay?
            </Typography>
          </Box>
        )}

        {state === 'phone-code' && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ mb: 2 }}>
              We're calling you now. Enter the five-digit code you hear.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Code expires in 10 minutes.
            </Typography>
            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
                setVerificationCode(value);
                if (codeError) {
                  setCodeError('');
                }
              }}
              error={!!codeError}
              helperText={codeError}
              disabled={loading}
              autoComplete="one-time-code"
              inputProps={{
                inputMode: 'numeric',
                maxLength: CODE_LENGTH,
              }}
            />
          </Box>
        )}

        {state === 'phone-throttled' && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ mb: 2 }}>
              We've already called this number several times today. Please try again tomorrow or contact us.
            </Typography>
          </Box>
        )}

        {state === 'already-claimed' && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This business has already been claimed.
            </Alert>
            <Typography>
              If you believe this is in error, please contact our support team for assistance.
            </Typography>
          </Box>
        )}

        {state === 'throttled' && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              A claim request for this business was made very recently.
            </Alert>
            <Typography>
              Please check the business email inbox for a verification link or try again in a few moments.
            </Typography>
          </Box>
        )}

        {state === 'error' && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {state === 'form' && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!isFormValid || loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Submit
            </Button>
          </>
        )}

        {state === 'success' && (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        )}

        {state === 'contact-us' && !userConsented && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              No, Thanks
            </Button>
            <Button
              onClick={handleContactUsYes}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Yes, Contact Me
            </Button>
          </>
        )}

        {state === 'phone-offer' && (
          <>
            <Button onClick={handlePhoneOfferNo} disabled={loading}>
              No
            </Button>
            <Button
              onClick={handlePhoneOfferYes}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Yes, Call Me
            </Button>
          </>
        )}

        {state === 'phone-code' && (
          <>
            <Button
              onClick={handleRequestCode}
              disabled={loading}
              sx={{ mr: 'auto', textTransform: 'none', textDecoration: 'underline' }}
              variant="text"
            >
              Call Again
            </Button>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleVerifyCode}
              variant="contained"
              disabled={verificationCode.length !== CODE_LENGTH || loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Verify
            </Button>
          </>
        )}

        {state === 'contact-us' && userConsented && (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        )}

        {state === 'phone-throttled' && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Close
            </Button>
            <Button
              onClick={() => setState('contact-us')}
              variant="contained"
              disabled={loading}
            >
              Contact Us
            </Button>
          </>
        )}

        {(state === 'already-claimed' || state === 'throttled') && (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        )}

        {state === 'error' && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Close
            </Button>
            <Button
              onClick={() => setState('form')}
              variant="contained"
              disabled={loading}
            >
              Try Again
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ClaimBusinessDialog;
